import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { combineLatest, Observable } from 'rxjs';
import {
  distinctUntilChanged,
  exhaustMap,
  filter,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { tapResponse } from '@ngrx/operators';

import { IProduto, IApiPaginator, LayoutUtilsService } from '@vip/core';
import { BuscaApiService } from '@vip/api';
import { CentroDistribuicaoFacade } from '@vip/state/centro-distribuicao';
import { BuscaState } from './busca-state.interface';
import { FiltroFacade } from '@vip/state/filtro';
import { BucketsFacade } from '@vip/state/buckets';
import {
  GenericComponentStore,
  GenericStoreStatus,
  IGenericState,
} from '@vip/state/utils';
import { AnalyticsFacade } from '@vip/state/analytics';
import { ProdutoFacade } from '@vip/state/produto';
import { ParametrosFacade } from '@vip/state/parametros';
import { OrdenacaoFacade } from '@vip/state/ordenacao';
interface buscaProdutosPayload {
  termo: string;
  filtro: string;
  page: number;
  forceReload?: boolean;
  limit?: number;
  searchSort?: number;
  shouldResetPagination?: boolean;
}

@Injectable()
export class BuscaStore extends GenericComponentStore<BuscaState> {
  private _lastSearchTerm = '';
  private _lastSearchFilter = '';

  readonly produtos$ = this.select((state) => state.data.produtos);
  readonly paginator$ = this.select((state) => state.data.paginator);
  readonly loading$ = this.select((state) => state.status === 'loading');
  readonly loadingSearchFilters$ = this.select(
    (state) => state.data.loadingSearchFilters,
  );
  readonly loadingNewSearchTerm$ = this.select(
    (state) => state.data.loadingNewSearchTerm,
  );
  readonly pending$ = this.select((state) => state.status === 'pending');
  readonly error$ = this.select((state) => state.error);
  readonly termos$ = this.select((state) => state.data.termos).pipe(
    filter((termo): termo is string[] => termo !== undefined),
  );
  readonly termosPesquisados$ = this.select(
    (state) => state.data.termosPesquisados,
  ).pipe(filter((termo): termo is string[] => termo !== undefined));

  readonly page$ = this.select(
    (stateContext: IGenericState<BuscaState>) =>
      stateContext.data.paginator.page,
  );

  private initialPaginator = {
    page: 1,
    items_per_page: 52,
    total_items: 0,
    total_pages: 1,
  };

  readonly setTermoPesquisado = this.updater(
    (
      state,
      data: {
        termosPesquisados: string[];
        page?: number;
      },
    ) => {
      if (
        state.data.termosPesquisados?.toString() ===
        data.termosPesquisados.toString()
      ) {
        return {
          ...state,
          data: {
            ...state.data,
            paginator: data.page
              ? { ...state.data.paginator, page: data.page }
              : state.data.paginator,
          },
          status: 'success',
        };
      }
      return {
        ...state,
        data: {
          ...state.data,
          produtos: [],
          paginator: this.initialPaginator,
          termosPesquisados: data.termosPesquisados,
        },
        status: 'success',
      };
    },
  );

  readonly setData = this.updater(
    (
      state,
      data: {
        produtos: IProduto[];
        paginator: IApiPaginator;
        termos?: string[];
        naoConcatenarProdutos?: boolean;
      },
    ) => {
      if (data.paginator && data.paginator?.page > 1) {
        if (!data.naoConcatenarProdutos) {
          return {
            ...state,
            data: {
              ...state.data,
              produtos: [...state.data.produtos, ...data.produtos],
              paginator: data.paginator,
              termos: data.termos,
              termosPesquisados: state.data.termosPesquisados,
            },
            status: 'success',
          };
        } else {
          return {
            ...state,
            data: {
              ...state.data,
              produtos: [...data.produtos],
              paginator: data.paginator,
              termos: data.termos,
              termosPesquisados: state.data.termosPesquisados,
            },
            status: 'success',
          };
        }
      }
      return {
        ...state,
        data: {
          ...state.data,
          ...data,
          termosPesquisados: state.data.termosPesquisados,
        },
        status: 'success',
      };
    },
  );

  readonly setPartialData = this.updater(
    (
      state,
      data: Partial<{
        produtos: IProduto[];
        paginator: IApiPaginator;
        termos: string[];
      }>,
    ) => {
      return {
        ...state,
        data: {
          ...state.data,
          ...data,
          produtos: data.produtos || state.data.produtos,
          paginator: data.paginator || state.data.paginator,
        },
        status: 'success',
      };
    },
  );

  readonly setLoadingBuscaState = this.updater(
    (
      state,
      data: {
        loadingSearchFilters: boolean;
        loadingNewSearchTerm: boolean;
      },
    ) => ({
      ...state,
      data: {
        ...state.data,
        loadingSearchFilters: data.loadingSearchFilters,
        loadingNewSearchTerm: data.loadingNewSearchTerm,
      },
    }),
  );

  readonly setStatus = this.updater((state, status: GenericStoreStatus) => ({
    ...state,
    status,
  }));

  readonly setError = this.updater((state, error: string) => ({
    ...state,
    error,
    produtos: [],
    status: 'error',
  }));

  readonly buscaProdutos = this.effect(
    (payload$: Observable<buscaProdutosPayload>) => {
      return combineLatest([
        payload$,
        this.cdFacade.filialECdSelecionado$.pipe(
          filter(
            ([filial, cdSelecionado]) =>
              filial !== undefined && cdSelecionado !== undefined,
          ),
        ),
        this.bucketsFacade.bucketProduto$.pipe(
          filter((bucket) => bucket !== ''),
        ),
        this.page$.pipe(filter((page) => page !== 0)),
        combineLatest([
          this.parametrosFacade.featureFlagBuscaSmarthintV2$.pipe(
            startWith(false),
          ),
          this.parametrosFacade.shCode$.pipe(startWith('')),
        ]),
      ]).pipe(
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
        filter(([payload]) => !!payload.termo.trim()),
        tap(() => this.setStatus('loading')),
        switchMap(
          ([
            payload,
            [filial, cdSelecionado],
            bucket,
            page,
            [featureFlag, shCode],
          ]) => {
            this.removeResetFromFilter(payload);

            const isNewSearchTerm = payload.termo !== this._lastSearchTerm;
            const isNewSearchOrFilter =
              isNewSearchTerm || payload.filtro !== this._lastSearchFilter;

            this.setLoadingBuscaState({
              loadingSearchFilters: isNewSearchOrFilter,
              loadingNewSearchTerm: isNewSearchTerm,
            });

            this.analyticsFacade.buscarItem(payload.termo);

            if (featureFlag && shCode) {
              const { filtro, searchSort } = this.extractSearchSortFromFiltro(
                payload.filtro,
                payload.searchSort,
              );

              return this.buscaApiService
                .searchProdutosByTermoSmarthintV2(
                  filial.id,
                  cdSelecionado.id,
                  payload.termo,
                  {
                    page: payload.shouldResetPagination ? 1 : page,
                    limit: payload.limit ?? 40,
                    searchSort,
                    filtro,
                  },
                )
                .pipe(
                  tapResponse(
                    (response) => {
                      this.setLoadingBuscaState({
                        loadingSearchFilters: false,
                        loadingNewSearchTerm: false,
                      });

                      if (isNewSearchOrFilter) {
                        this._lastSearchTerm = payload.termo;
                        this._lastSearchFilter = payload.filtro;

                        this.filtroFacade.getFiltroBusca(
                          0,
                          filtro,
                          response.filters,
                        );

                        this.ordenacaoFacade.setOrdenacoesBusca(
                          response.sorts ?? [],
                        );
                      }

                      this.setData({
                        produtos: response.data
                          ? response.data.map((produto) => {
                              return this.mapProdutos(produto, bucket);
                            })
                          : [],
                        termos: [payload.termo],
                        paginator: response.paginator || this.initialPaginator,
                        naoConcatenarProdutos: false,
                      });
                      this.produtosFacade?.enviaImpressao(response.data ?? []);
                    },
                    (error) => {
                      this.setLoadingBuscaState({
                        loadingSearchFilters: false,
                        loadingNewSearchTerm: false,
                      });
                      this.setError((error as HttpErrorResponse)?.error?.error);
                    },
                  ),
                );
            }

            return this.buscaApiService
              .searchProdutosByTermo(
                filial.id,
                cdSelecionado.id,
                payload.termo,
                'page=' + (payload.shouldResetPagination ? 1 : page),
                payload.filtro,
              )
              .pipe(
                tapResponse(
                  (response) => {
                    this.setLoadingBuscaState({
                      loadingSearchFilters: false,
                      loadingNewSearchTerm: false,
                    });

                    if (isNewSearchOrFilter) {
                      this._lastSearchTerm = payload.termo;
                      this._lastSearchFilter = payload.filtro;

                      this.filtroFacade.getFiltroBusca(
                        response.data.busca_id,
                        payload.filtro,
                        response.data.filtros,
                      );
                    }

                    this.setData({
                      produtos: response.data.produtos
                        ? response.data.produtos?.map((produto) => {
                            return this.mapProdutos(produto, bucket);
                          })
                        : [],
                      termos: [response.data.termo],
                      paginator: response.paginator || this.initialPaginator,
                      naoConcatenarProdutos: false,
                    });
                    this.produtosFacade?.enviaImpressao(
                      response.data.produtos ?? [],
                    );
                  },
                  (error) => {
                    this.setLoadingBuscaState({
                      loadingSearchFilters: false,
                      loadingNewSearchTerm: false,
                    });
                    this.setError((error as HttpErrorResponse)?.error?.error);
                  },
                ),
              );
          },
        ),
      );
    },
  );

  readonly getSuggestionsByTermo = this.effect(
    (payload$: Observable<{ termo: string }>) => {
      return combineLatest([
        payload$,
        this.cdFacade.filialECdSelecionado$.pipe(
          filter(
            ([filial, cdSelecionado]) =>
              filial !== undefined && cdSelecionado !== undefined,
          ),
        ),
      ]).pipe(
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
        tap(() => this.setStatus('loading')),
        switchMap(([payload, [filial]]) =>
          this.buscaApiService
            .getSuggestionsByTermo(filial.id, payload.termo)
            .pipe(
              tapResponse(
                (response) => {
                  this.setStatus('success');
                  this.setPartialData({
                    termos: response.data.map((termos) => termos.termo),
                  });
                },
                () => {
                  this.setStatus('error');
                  this.setPartialData({
                    termos: [],
                  });
                },
              ),
            ),
        ),
      );
    },
  );

  nextPage() {
    this.patchState((state) => ({
      data: {
        ...state.data,
        paginator: {
          ...state.data.paginator,
          page:
            state.data.paginator.page < state.data.paginator.total_pages
              ? state.data.paginator.page + 1
              : state.data.paginator.page,
          total_pages: state.data.paginator.total_pages,
        },
        produtos: state.data.produtos,
      },
    }));
  }

  firstPage() {
    this.patchState((state) => ({
      data: {
        ...state.data,
        paginator: this.initialPaginator,
      },
    }));
  }

  goToPage(page: number) {
    this.patchState((state) => ({
      data: {
        ...state.data,
        paginator:
          state.data.paginator.page !== page
            ? {
                ...state.data.paginator,
                page,
              }
            : state.data.paginator,
        produtos: state.data.paginator.page !== page ? [] : state.data.produtos,
      },
    }));
  }

  goToPageFirstTime(page: number) {
    this.patchState((state) => ({
      data: {
        ...state.data,
        paginator: {
          ...state.data.paginator,
          page,
          total_pages: page,
        },
        produtos: [],
      },
    }));
  }

  resetLoadingStates(): void {
    this._lastSearchTerm = '';
    this._lastSearchFilter = '';
    this.setLoadingBuscaState({
      loadingSearchFilters: false,
      loadingNewSearchTerm: false,
    });
  }

  constructor(
    private bucketsFacade: BucketsFacade,
    private buscaApiService: BuscaApiService,
    private filtroFacade: FiltroFacade,
    private cdFacade: CentroDistribuicaoFacade,
    private analyticsFacade: AnalyticsFacade,
    private layoutUtilsService: LayoutUtilsService,
    private parametrosFacade: ParametrosFacade,
    private ordenacaoFacade: OrdenacaoFacade,
    private produtosFacade?: ProdutoFacade,
  ) {
    super({
      produtos: [],
      paginator: {
        page: 1,
        items_per_page: 52,
        total_items: 0,
        total_pages: 1,
      },
      loadingSearchFilters: false,
      loadingNewSearchTerm: false,
    });
  }

  protected mapProdutos(produto: IProduto, bucket?: string) {
    return {
      ...produto,
      imagem: produto.imagem ? `${bucket}/${produto.imagem}` : produto.imagem,
    };
  }

  private removeResetFromFilter(payload: buscaProdutosPayload): void {
    payload.filtro = payload.filtro.replace('&reset', '');
  }

  private extractSearchSortFromFiltro(
    filtro: string,
    searchSort?: number,
  ): { filtro: string; searchSort: number } {
    if (typeof searchSort === 'number') {
      return { filtro, searchSort };
    }

    const parts = filtro
      .split('&')
      .map((part) => part.trim())
      .filter((part) => part && part !== 'reset');
    let mappedSearchSort: number | undefined;
    const remaining: string[] = [];

    parts.forEach((part) => {
      if (part.startsWith('orderby=')) {
        const orderValue = decodeURIComponent(part.replace('orderby=', ''));
        mappedSearchSort = Number(orderValue) ?? 0;
        return;
      }
      remaining.push(part);
    });

    return {
      filtro: remaining.join('&'),
      searchSort: mappedSearchSort ?? 0,
    };
  }
}
